import { formatMessage, randomMessage } from "model/message";
import clock from "util/clock";

const MAX_HEALTH_GAIN = 5;
const ActionType = "Sleep";

/**
 * Sleep reports the body, and nothing else here may.
 *
 * The world is already speaking on this same click: calendar.nextStep flips the light, and the
 * clock is pushing town ambient — which now carries the dusk and dawn lines too — through the
 * hours. A line here about the dark, the dawn or the town would be an echo of one the player is
 * reading at that moment. This is the man lying down and the man getting up.
 *
 * Nothing may presume a bed or a roof either — takeBowl.js runs this action for the collapse
 * in the first town, where he is face down in the street with a dog's bowl.
 */

// Says which of the two is missing rather than naming both and sending the player off to fetch
// them — the old line read `need food and water`, which is an instruction, not a thing seen.
//
// In practice only the hungry line shows: this is a city action, and `caravan.consumption`
// takes water down to zero in a town, so thirst cannot hold sleep there. The other two are
// what the function owes its arguments, not decoration — if a town ever charges for water
// they are already written.
const restless = ({ hungry, thirsty }) => {
  if (hungry && thirsty) {
    return `nothing to eat and nothing to drink. the hours pass and no rest in them.`;
  }
  if (hungry) return `the gut is empty and will not be quiet. no rest in it.`;
  return `dry mouth, dry throat. no rest in it.`;
};

const message = ({ success, gain, lack }) => {
  if (!success) {
    return formatMessage(restless(lack), {});
  }
  // Health is full, so the gain came back zero. Say that nothing happened without reading out
  // the number that did not move.
  if (gain.health === 0) {
    return formatMessage(`lie down whole. nothing in the body wants mending.`, {});
  }

  const message = randomMessage([
    "hours of nothing. the ache goes out of the shoulders.",
    "wake with the stiffness gone out of the legs.",
    "the swelling goes out of the feet.",
    "lie still a long while and something in the chest loosens.",
    "the body takes what it is given.",
  ]);
  return formatMessage(message, { gain });
};

// Returns what is short rather than a bare boolean, so the failure line can be specific.
const canSleep = ({ caravan, resources, phase, city }) => {
  const { attribs, isVisible } = resources;
  const consumption = caravan.consumption(city);
  const canEat = attribs.food.value >= consumption.food ||
    !isVisible(phase, attribs.food);
  const canDrink = attribs.water.value >= consumption.water ||
    !isVisible(phase, attribs.water);

  return { hungry: !canEat, thirsty: !canDrink };
};

const attempt = ({ party }) => {
  const lack = canSleep(party);
  const result = {
    success: !lack.hungry && !lack.thirsty,
    lack,
    cost: {},
    gain: {
      health: 0,
    },
  };

  if (!result.success) return result;

  result.gain.health = Math.round(
    Math.min(
      100 - party.resources.attribs.health.value,
      Math.ceil(Math.random() * MAX_HEALTH_GAIN),
    ),
  );

  return result;
};

const executeSideEffects = (context, result) => {
  const { party, calendar } = context;
  const { gain } = result;
  const { history, resources } = party;

  // Track action
  history.add({ action: "cityRest", result });

  // Update resources
  resources.delta({ ...gain }, context);

  // Time passes
  // The player bought this phase. Rebaseline the clock so the accumulator, which may
  // already be nearly full, does not hand over a second phase moments later.
  calendar.nextStep(context);
  clock.reset();

  // notify of the results
  return {
    message: message(result),
  };
};

const action = {
  title: "Sleep",
  hotkey: "s",
  type: ActionType,
  enabled: () => true,
  execute: (context) => {
    const result = attempt(context);
    const { message } = executeSideEffects(context, result);

    return { result: { message } };
  },
};

export default action;
